import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

//hooks
import useQueryFleetTracking from "../../hooks/fleet/useFleetTracking";
import useTheme from "../../utils/useTheme";
import "./fleetDashboard.css";

import FleetCardsList from "./FleetCardsList";
import FleetViewMap from "./FleetViewMap";
import FleetViewError from "./FleetViewError";
import FleetViewAlert from "./FleetViewAlert";
import FleetViewDetails from "./FleetViewDetails";
import FloatingMenus from "../../component/homepage/FloatingMenus";
import FleetMessageList from "../../component/fleet-message/FleetMessageList";

import FleetViewPanel from "./FleetViewPanel";
import TestFleetCard from "./TestFleetCard";

import imgFleetLink from "../../assets/icon_fleet_link.png";

const FleetDashboard = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const adminId = sessionStorage.getItem("id");

  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);

  const [trackingFleetId, setTrackingFleetId] = useState(null);
  const [selectedFleetId, setSelectedFleetId] = useState(null);

  //floating menu
  const [showVehicleList, setShowVehicleList] = useState(true);

  //for fleet message
  const [showMessages, setShowMessages] = useState(false);
  const [messageVehicle, setMessageVehicle] = useState(null); // for deep-link from FleetViewDetails

  const {
    fleetList,
    fleetMap,
    detailsMap,
    mergedMap,
    stats,
    isLoading,
    isError,
  } = useQueryFleetTracking(adminId, 5000);

  if (import.meta.env.DEV) {
    console.log("debug: ujj FleetDashboard fleetlist:", { fleetList });
  }

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(
        "debug: ujj FleetDashboard detailsMap keys:",
        Object.keys(detailsMap),
        { detailsMap },
      );
    }
  }, [detailsMap]);

  const handleMapReady = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const selectedFleet = useMemo(() => {
    return selectedFleetId ? mergedMap[selectedFleetId] : null;
  }, [selectedFleetId, mergedMap]);

  // useCallback ensures stable function reference
  // prevents unnecessary re-renders in child components (MapMarker, List)
  const handleFleetSelect = useCallback(
    (vehicleId) => {
      setSelectedFleetId(vehicleId);
      const fleet = mergedMap[vehicleId];

      if (!fleet || fleet.lat == null || fleet.lng == null || !mapInstance) {
        return;
      }

      const lat = Number(fleet.lat);
      const lng = Number(fleet.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      const currentZoom = mapInstance.getZoom();

      // Center map on selected fleet record.
      // Coordinates from MongoDB are [lng, lat], Leaflet needs [lat, lng].
      // mapInstance.setView([lat, lng], 15, {
      //   animate: true,
      //   duration: 0.8,
      // });

      // Cancel any tracking animation still in progress.
      mapInstance.stop();

      mapInstance.easeTo({
        center: [lng, lat],
        zoom: 16,
        duration: 800,
        essential: true,
      });
    },
    [mapInstance, mergedMap],
  );

  const handleClearFleetSelect = useCallback(() => {
    setSelectedFleetId(null);
  }, []);

  const handleClearFleetTracking = useCallback(() => {
    setTrackingFleetId(null);
  }, []);

  //for map tracking
  // Using mergedMap gives O(1) lookup instead of O(n)
  const trackingFleet = trackingFleetId ? mergedMap[trackingFleetId] : null;

  // AFTER — MapTracker expects { lat, lng }
  const trackingPositionss =
    trackingFleet?.lat != null && trackingFleet?.lng != null
      ? {
          lat: trackingFleet.lat,
          lng: trackingFleet.lng,
          bearing: trackingFleet.bearing ?? trackingFleet.heading ?? 0,
        }
      : null;

  const trackingPosition = useMemo(() => {
    if (trackingFleet?.lat == null || trackingFleet?.lng == null) {
      return null;
    }

    const lat = Number(trackingFleet.lat);
    const lng = Number(trackingFleet.lng);
    const bearing = Number(trackingFleet.trackingBearing);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return {
      lat,
      lng,
      bearing: Number.isFinite(bearing) ? bearing : 0,
    };
  }, [trackingFleet?.lat, trackingFleet?.lng, trackingFleet?.trackingBearing]);

  const isInitialLoading = isLoading && fleetList.length === 0;

  const handleTrackToggle = useCallback((vehicleId) => {
    setTrackingFleetId((previousId) =>
      previousId === vehicleId ? null : vehicleId,
    );
  }, []);

  //floating menus
  const handleVehicleClick = () => {
    // details panel is open
    if (selectedFleetId) {
      handleClearFleetSelect();
      setShowVehicleList(true);
      return;
    }
    setShowVehicleList((prevShowVehicleList) => !prevShowVehicleList);
  };
  const onFleetTableIconClick = () => {
    const navigateUrl = "/fleettable";
    navigate(navigateUrl);
  };

  const onHistoryIconClick = () => {
    const navigateUrl = "/history";
    navigate(navigateUrl);
  };

  const onFleetManagerIconClick = () => {
    const navigateUrl = "/fleetmanager";
    navigate(navigateUrl);
  };

  const onAccountIconClick = () => {
    const navigateUrl = "/my-profile";
    navigate(navigateUrl);
  };

  const onHelpIconClick = () => {
    const navigateUrl = "/help";
    navigate(navigateUrl);
  };

  if (isError) {
    return (
      <FleetViewError
        title={"Connection Interrupted"}
        message={
          "We're having trouble reaching the tracking servers. Please check your network."
        }
        action="Try Again"
        handleOnClick={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="fleet-dashboard">
      <div className="map-section">
        <FleetViewMap
          mapRef={mapRef}
          fleetList={fleetList}
          selectedFleet={selectedFleet}
          trackingFleetId={trackingFleetId}
          trackingPosition={trackingPosition}
          isInitialLoading={isInitialLoading}
          isTracking={trackingFleetId != null}
          onMapReady={handleMapReady}
          onFleetSelect={handleFleetSelect}
          onTrack={setTrackingFleetId}
          onStopTracking={handleClearFleetTracking}
        />

        {/* <FleetViewPanel stats={stats} /> */}
      </div>

      {/* Theme Toggle Button */}
      <button className="theme-toggle-btn" onClick={toggleTheme}>
        <i className={`bi ${theme === "light" ? "bi-sun" : "bi-moon-stars"}`} />
      </button>

      <FleetViewAlert fleetList={fleetList} onFleetSelect={handleFleetSelect} />

      {/* <TestFleetCard /> */}

      <FleetCardsList
        fleetList={fleetList}
        collapsed={!showVehicleList}
        onFleetSelect={handleFleetSelect}
        selectedFleetId={selectedFleetId}
        onFleetTrack={handleTrackToggle}
        trackingFleetId={trackingFleetId}
        isLoading={isLoading}
        onClose={() => setShowVehicleList(false)}
      />

      <FleetViewDetails
        selectedFleet={selectedFleet}
        onClose={handleClearFleetSelect}
      />

      <FloatingMenus
        activeKey={"vehicles"}
        onVehicleIconClick={handleVehicleClick}
        // onAlertIconClick={handleAlertIconClick}
        onFleetTableIconClick={onFleetTableIconClick}
        onHistoryIconClick={onHistoryIconClick}
        onFleetManagerIconClick={onFleetManagerIconClick}
        onAccountIconClick={onAccountIconClick}
        onHelpIconClick={onHelpIconClick}
        badges={{
          vehicles: fleetList.length,
          // alerts: alerts.length,
          alerts: 0,
        }}
      />

      <button
        className="floating-chat-btn"
        onClick={() => {
          setMessageVehicle(null);
          setShowMessages(true);
        }}
        title="Messages"
      >
        {/* <i className="bi bi-chat-dots-fill" /> */}
        <img src={imgFleetLink} className="fd-fleet-link" alt="employee" />
      </button>

      <button
        className="map-control-btn-bbox"
        onClick={() => mapRef.current?.fitBounds()}
        title="Center Fleet View"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M15 3h6v6M9 21H3v-6M21 15v6h-6M3 9V3h6" />
          <path d="M21 3l-7 7M3 21l7-7M3 3l7 7M21 21l-7-7" />
        </svg>
      </button>

      {showMessages && (
        <FleetMessageList
          defaultVehicle={messageVehicle}
          fleetList={fleetList}
          onClose={() => {
            setShowMessages(false);
            setMessageVehicle(null);
          }}
        />
      )}
    </div>
  );
};

export default FleetDashboard;
